import { FC, useState } from "react";
import { X } from "lucide-react";
import { Button } from "src/components/ui/button";

interface Props {
	groups: { id: string | number; name: string }[];
	allGroups: Group[];
	// .NET roles use GUID string ids; legacy Django roles used numeric ids.
	// Accept either for forward/backward compatibility.
	onAssign: (groupIds: (string | number)[]) => void;
	onRemove: (groupIds: (string | number)[]) => void;
	isAssigning: boolean;
	isRemoving?: boolean;
}

const PersonGroupsTab: FC<Props> = ({
	groups,
	allGroups,
	onAssign,
	onRemove,
	isAssigning,
	isRemoving = false
}) => {
	const [selectedGroupId, setSelectedGroupId] = useState<string>("");

	const assignedIds = new Set(groups.map((g) => g.id));
	const availableGroups = allGroups.filter((g) => !assignedIds.has(g.id));

	const handleAdd = () => {
		if (!selectedGroupId) return;
		// Pass through the raw value — the backend accepts both numeric
		// legacy ids and GUID strings.
		onAssign([selectedGroupId]);
	};

	return (
		<div className="flex flex-col gap-4">
			{/* Assigned groups */}
			<div className="rounded-xl border border-border bg-subtle shadow-sm p-6">
				<label className="font-heading text-lg font-medium text-foreground mb-4 block">
					Assigned Groups
				</label>

				{groups.length === 0 ? (
					<p className="text-sm text-zinc-600">No groups assigned.</p>
				) : (
					<div className="flex flex-wrap gap-2">
						{groups.map((group) => (
							<span
								key={group.id}
								className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium bg-primary/10 text-primary border border-primary/20"
							>
								{group.name}
								<button
									type="button"
									onClick={() => onRemove([group.id])}
									disabled={isRemoving}
									className="text-primary/60 hover:text-primary transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
									aria-label={`Remove ${group.name}`}
								>
									<X className="h-3.5 w-3.5" />
								</button>
							</span>
						))}
					</div>
				)}
			</div>

			{/* Add group */}
			{availableGroups.length > 0 && (
				<div className="rounded-xl border border-border bg-subtle shadow-sm p-6">
					<label className="font-heading text-lg font-medium text-foreground mb-4 block">
						Add Group
					</label>
					<div className="flex items-center gap-3">
						<select
							className="flex h-9 flex-1 items-center rounded-md border border-border bg-white px-3 py-2 text-sm shadow-sm focus:outline-none focus:ring-1 focus:ring-ring"
							value={selectedGroupId}
							onChange={(e) => setSelectedGroupId(e.target.value)}
						>
							<option value="">Select a group</option>
							{availableGroups.map((group) => (
								<option key={group.id} value={String(group.id)}>
									{group.name}
								</option>
							))}
						</select>
						<Button
							type="button"
							onClick={handleAdd}
							disabled={!selectedGroupId || isAssigning}
						>
							{isAssigning ? (
								<div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
							) : (
								"Add"
							)}
						</Button>
					</div>
				</div>
			)}
		</div>
	);
};

export default PersonGroupsTab;
